/**
 * User Message Utilities
 * 
 * Features:
 * - Content filtering to remove JSON from user messages
 * - Preserves user's intended text while hiding technical JSON data
 * - Maintains message readability and cleanliness
 * 
 * Logic:
 * - Removes JSON code blocks (```json...```)
 * - Removes inline JSON objects ({...})
 * - Preserves all other text content
 * - Cleans up extra whitespace after JSON removal
 * 
 * Changes:
 * - Created utility to filter JSON content from user messages
 * - Prevents display of technical JSON data in user message bubbles
 * - Ensures user messages remain clean and readable
 */

/**
 * Removes JSON content from user message text
 * @param content The original message content
 * @returns Content with JSON removed
 */
export const filterJsonFromUserMessage = (content: string): string => {
  if (!content || typeof content !== 'string') {
    return '';
  }

  let filteredContent = content;

  // Remove JSON code blocks (```json...```)
  const jsonCodeBlockRegex = /```json\s*[\s\S]*?\s*```/g;
  filteredContent = filteredContent.replace(jsonCodeBlockRegex, '');

  // Remove inline JSON objects (basic detection for {...})
  const inlineJsonRegex = /\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/g;
  const inlineMatches = filteredContent.match(inlineJsonRegex) || [];

  for (const inlineMatch of inlineMatches) {
    try {
      // Try to parse as JSON to confirm it's valid JSON
      JSON.parse(inlineMatch);
      // If successful, remove it from content
      filteredContent = filteredContent.replace(inlineMatch, '');
    } catch (e) {
      // If not valid JSON, leave it in the content
      // This preserves things like code snippets that might look like JSON but aren't
    }
  }

  // Clean up extra whitespace
  filteredContent = filteredContent
    .replace(/\n\s*\n\s*\n/g, '\n\n') // Replace multiple newlines with double newlines
    .trim();

  return filteredContent;
};
